import { useState, useEffect, useRef } from 'react';
import { signUp } from 'aws-amplify/auth';
import { validateEmail, validateNotEmpty } from '../Support/ValidationLogic';
import SharedButton from '@/global/Button/Button';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import useText from '@/hooks/useText';
import VerifyEmailPopup from './VerifyEmailPopUp';
import ResendConfirmationEmailButton from './ResendConfirmationEmailButton';

function ConfirmationPopup({
  onClose,
  onConfirm,
  isAcademic,
}: {
  onClose: () => void;
  onConfirm: (evt: any) => Promise<void>;
  isAcademic: boolean;
}) {
  return (
    <div className="RegisterPopup">
      <div className="apply-popup-container">
        <h6>
          {isAcademic
            ? ' Your application will be manually reviewed. Please double-check all information is complete & accurate. Incomplete or inaccurate information may result in rejection. What would you like to do?'
            : 'Please ensure all information is complete and accurate. What would you like to do?'}
        </h6>
        <SharedButton variant="secondary" onClick={onClose} className="button-review">
          Review & Edit
        </SharedButton>
        <SharedButton
          variant="gradient"
          onClick={onConfirm}
          className="button-home"
          aria-label="submit-modal"
        >
          Confirm & Submit
        </SharedButton>
      </div>
    </div>
  );
}

function SignUpForm() {
  const academicButton = useText('signup.apply_trial.academic_button');
  const advancedButton = useText('signup.apply_trial.advanced_button');

  const isAcademic = location.pathname === '/academic-apply';
  const [isChina, setIsChina] = useState(false);

  const [formErrorMessage, setFormErrorMessage] = useState(null);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);

  const [utmSource, setUtmSource] = useState('direct');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('utm_source');
    if (source) {
      setUtmSource(source);
      sessionStorage.setItem('signup_source', source);
    }
  }, []);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [organization, setOrganization] = useState('');
  const [fieldOfStudy, setFieldOfStudy] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [profile, setProfile] = useState('');
  const [purchaseIntent, setPurchaseIntent] = useState('');
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [academicLicenseAccepted, setAcademicLicenseAccepted] = useState(false);

  const [password, setPassword] = useState('');
  const [rpassword, setRpassword] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isRpasswordVisible, setIsRpasswordVisible] = useState(false);

  const [isNameValid, setIsNameValid] = useState(true);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [isOrganizationValid, setIsOrganizationValid] = useState(true);
  const [isPasswordValid, setIsPasswordValid] = useState(true);
  const [isPasswordMatching, setIsPasswordMatching] = useState(true);
  const [isRpasswordValid, setIsRpasswordValid] = useState(true);
  const [showVerifyEmailPopup, setShowVerifyEmailPopup] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [showResendForExistingUser, setShowResendForExistingUser] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);

  const normalizeEmail = (value: string) => value.trim().toLowerCase();

  //remove autocomplete and keep css
  useEffect(() => {
    const disableAutoComplete = input => {
      input.setAttribute('readonly', 'readonly');
      setTimeout(() => {
        input.removeAttribute('readonly');
      }, 100);
    };
    const inputs = document.querySelectorAll('input');
    inputs.forEach(input => disableAutoComplete(input));
  }, []);

  // LinkedIn Insight Tag
  useEffect(() => {
    const _linkedin_partner_id = '8934697';
    (window as any)._linkedin_data_partner_ids = (window as any)._linkedin_data_partner_ids || [];
    (window as any)._linkedin_data_partner_ids.push(_linkedin_partner_id);

    (function (l) {
      if (!l) {
        (window as any).lintrk = function (a, b) {
          (window as any).lintrk.q.push([a, b]);
        };
        (window as any).lintrk.q = [];
      }
      const s = document.getElementsByTagName('script')[0];
      if (s && s.parentNode) {
        const b = document.createElement('script');
        b.type = 'text/javascript';
        b.async = true;
        b.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
        s.parentNode.insertBefore(b, s);
      }
    })((window as any).lintrk);

    return () => {
      const scripts = document.querySelectorAll(
        'script[src="https://snap.licdn.com/li.lms-analytics/insight.min.js"]',
      );
      scripts.forEach(script => script.remove());
    };
  }, []);

  function resetForm() {
    setName('');
    setEmail('');
    setIsChina(false);
    setPhone('');
    setOrganization('');
    setFieldOfStudy('');
    setLinkedin('');
    setProfile('');
    setPurchaseIntent('');
    setPrivacyPolicyAccepted(false);
    setAcademicLicenseAccepted(false);
    setPassword('');
    setRpassword('');
    setIsPasswordVisible(false);
    setIsRpasswordVisible(false);
    setFormErrorMessage(null);
    setIsFormSubmitted(false);
  }

  function validateForm() {
    if (!privacyPolicyAccepted) {
      setFormErrorMessage('You must accept the privacy policy before submitting.');
      return false;
    }
    if (isAcademic && !academicLicenseAccepted) {
      setFormErrorMessage('You must accept the license agreement before submitting.');
      return false;
    }

    if (!isAcademic && !profile) {
      setFormErrorMessage('Please select a company profile');
      return false;
    }

    if (!isAcademic && !purchaseIntent) {
      setFormErrorMessage('Please select a purchase intent');
      return false;
    }

    if (isChina && !isPhoneValid) {
      setFormErrorMessage('Phone number should be +86 and 11 digits');
    }

    const textValidations = [
      validateNameInput(name),
      validateEmailInput(email),
      validateOrganizationInput(organization),
      validatePasswordsInput(password, rpassword),
    ];
    if (!textValidations.every(bool => bool === true)) {
      setFormErrorMessage('Check text inputs for errors');
      return false;
    }
    setFormErrorMessage('');
    return true;
  }

  const handleNameChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setName(evt.target.value);
  };

  const handleNameBlur = (evt: React.FocusEvent<HTMLInputElement>) => {
    validateNameInput(evt.target.value);
  };

  const nameRegex = /^[\p{L}\p{M}'´` -]+$/u;

  function validateNameInput(value: string) {
    const isValid = validateNotEmpty(value) && nameRegex.test(value);
    setIsNameValid(isValid);
    return isValid;
  }

  const handleEmailChange = evt => {
    const rawValue = evt.target.value || '';
    setEmail(rawValue.trimStart());
    if (rawValue.includes('.cn')) {
      setIsChina(true);
      setPhone(forceCnPhone(phone));
    } else {
      setIsChina(false);
    }
  };

  const handleEmailBlur = evt => {
    const isValid = validateEmailInput(evt.target.value);
  };

  function validateEmailInput(value) {
    const isValid = validateEmail(normalizeEmail(value), isAcademic);
    setIsEmailValid(isValid);
    return isValid;
  }

  // Phone input
  const forceCnPhone = value => {
    if (!value.startsWith('+86 ')) {
      if (value === '+86') {
        value = '+86 ';
      } else {
        value = '+86 ' + value;
      }
    }
    return value;
  };

  const handlePhoneChange = evt => {
    // only configured for chinese phone numbers
    let val = evt.target.value;
    if (isChina) {
      val = forceCnPhone(val);
    }
    setPhone(val);
  };

  const handlePhoneBlur = evt => {
    const isValid = validatePhoneInput(evt.target.value);
  };

  function validatePhoneInput(value) {
    const isValid = validateNotEmpty(value) && value.length === 15;
    setIsPhoneValid(isValid);
    return isValid;
  }

  // organization input
  const handleOrganizationChange = evt => {
    setOrganization(evt.target.value);
  };

  const handleOrganizationBlur = evt => {
    validateOrganizationInput(evt.target.value);
  };

  function validateOrganizationInput(value) {
    const isValid = validateNotEmpty(value);
    setIsOrganizationValid(isValid);
    return isValid;
  }

  // Field of study input
  const handlefieldOfStudyChange = evt => {
    setFieldOfStudy(evt.target.value);
  };

  // linkedin input
  const handlesupportingLinksChange = evt => {
    setLinkedin(evt.target.value);
  };

  // profile input
  const handleProfileChange = evt => {
    setProfile(evt.target.value);
  };

  // purchaseIntent input
  const handlePurchaseIntentChange = evt => {
    setPurchaseIntent(evt.target.value);
  };

  // checkboxes
  const handleAcademicLicenseCheckboxChange = evt => {
    setAcademicLicenseAccepted(evt.target.checked);
  };

  const handlePrivacyPolicyCheckboxChange = evt => {
    setPrivacyPolicyAccepted(evt.target.checked);
  };

  // password/rpassword input
  const handlePasswordChange = evt => {
    setPassword(evt.target.value);
  };

  const handlePasswordBlur = evt => {
    validatePasswordsInput(evt.target.value, rpassword);
  };

  const handleRpasswordChange = evt => {
    setRpassword(evt.target.value);
  };

  const handleRpasswordBlur = evt => {
    validatePasswordsInput(password, evt.target.value);
    // check rpassword for empty
    const isValidR = validateNotEmpty(rpassword);
    setIsRpasswordValid(isValidR);
  };

  function validatePasswordsInput(password, rpassword) {
    // check password for empty
    const isValid = validateNotEmpty(password);
    setIsPasswordValid(isValid);

    // check if passwords match
    let doPasswordsMatch;
    if (password && rpassword) {
      doPasswordsMatch = password === rpassword;
      setIsPasswordMatching(doPasswordsMatch);
    }
    return isValid && doPasswordsMatch;
  }

  const handleOpenConfirmationPopup = () => {
    if (validateForm()) {
      setShowConfirmationPopup(true);
    }
  };

  async function handleSubmit(evt) {
    evt.preventDefault();
    if (submitInFlightRef.current || isSubmitting) {
      return;
    }
    submitInFlightRef.current = true;

    const created_at = new Date().getTime().toString();
    const normalizedEmail = normalizeEmail(email);

    const source = sessionStorage.getItem('signup_source') || utmSource;

    const payload = {
      username: normalizedEmail,
      password: password,
      options: {
        userAttributes: {
          email: normalizedEmail,
          phone_number: phone.replace(/\s+/g, ''),
          'custom:company': organization,
          'custom:name': name,
          'custom:created_at': created_at,
          'custom:linkedin': linkedin,
          'custom:field_of_study': fieldOfStudy,
          profile,
          'custom:purchase_intent': purchaseIntent,
          'custom:application_role': isAcademic ? 'ROLE_ACADEMIC' : 'ROLE_TRIAL',
          'custom:source': source,
        },
      },
    };

    if (!validateForm()) {
      submitInFlightRef.current = false;
      return;
    }

    setFormErrorMessage('Sending...');
    setIsSubmitting(true);
    try {
      await signUp(payload);
      setRegisteredEmail(normalizedEmail);
      resetForm();
      setShowVerifyEmailPopup(true);
      setIsFormSubmitted(true);
      setShowResendForExistingUser(false);
      setShowConfirmationPopup(false);
      sessionStorage.removeItem('signup_source');
    } catch (e) {
      const usernameExists = e?.name === 'UsernameExistsException' || e?.code === 'UsernameExistsException';
      if (usernameExists) {
        setRegisteredEmail(normalizedEmail);
        setShowResendForExistingUser(true);
        setFormErrorMessage(
          'This email is already registered. If you have not confirmed your account yet, please check your inbox or resend the confirmation email below.',
        );
      } else {
        setShowResendForExistingUser(false);
        setFormErrorMessage(
          e?.message ||
            'There was an error with application submission. Please try again or contact us by email at info@disgenet.com',
        );
      }
    } finally {
      setIsSubmitting(false);
      submitInFlightRef.current = false;
    }
  }

  return (
    <>
      <div className="form-apply w-full max-w-2xl rounded-2xl">
        <h6 className="text-center mb-4 text-xl font-semibold flex items-center justify-center gap-2">
          Sign up form
          <img src="/static/images/icons/user-pen-lucide.svg" alt="User Pen" className="w-6 h-6" />
        </h6>
        <form onSubmit={handleSubmit}>
          {isFormSubmitted && (
            <div className="form-success">
              {isAcademic
                ? 'Check your inbox within 24hrs to complete your application!'
                : 'Registration completed!'}
            </div>
          )}
          {/* Full name and email */}
          <div className="flex mb-4 gap-2">
            <div className="text-left form-q">
              <label htmlFor="name" className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">
                full name *
              </label>
              <input
                className={`form-input ${!isNameValid ? 'invalid-input' : ''}`}
                aria-label="name"
                id="name"
                type="text"
                placeholder="Full Name..."
                onChange={handleNameChange}
                onBlur={handleNameBlur}
                value={name}
                data-validate="Name is required"
              />
              <div>{!isNameValid && <p className="apply-input-error">Please enter a valid name</p>}</div>
            </div>
            <div className="text-left form-q">
              <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase" htmlFor="email">
                email *
              </label>
              <input
                className="form-input"
                aria-label="email"
                type="text"
                id="email"
                placeholder="Email address..."
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                value={email}
                data-validate="Valid email is required: ex@abc.xyz"
              />
              {!isEmailValid && (
                <p className="apply-input-error">
                  {isAcademic
                    ? 'Please enter a valid institutional email'
                    : 'Only academic or business emails are accepted'}
                </p>
              )}
            </div>
          </div>
          {/* If china, phone number is required */}
          {isChina && (
            <div className="text-left form-q">
              <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">
                Phone number *
              </label>
              <div className="line-input" data-validate="Valid phone number is required">
                <input
                  className="form-input"
                  aria-label="phone"
                  type="text"
                  placeholder="Phone number..."
                  onChange={handlePhoneChange}
                  onBlur={handlePhoneBlur}
                  value={phone}
                />
              </div>
              <div>
                {!isPhoneValid && (
                  <p className="apply-input-error ">
                    Please enter a valid phone number with no special characters
                  </p>
                )}
              </div>
              <p>
                Our official distributor, <a href="cloudscientific.com">Cloudscientific</a>, is here to assist
                you. Contact them at: +86 02154975000
              </p>
            </div>
          )}
          {/* Organization and field of study */}
          <div className="flex mb-4 gap-2">
            <div className="text-left  form-q">
              <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">
                Organization *
              </label>
              <div data-validate="Name is required">
                <input
                  className="form-input"
                  aria-label="organization"
                  type="text"
                  placeholder={isAcademic ? 'University, research institute...' : 'Company...'}
                  onChange={handleOrganizationChange}
                  onBlur={handleOrganizationBlur}
                  value={organization}
                />
              </div>
              <div>
                {!isOrganizationValid && (
                  <p className="apply-input-error">Please enter your organization&apos;s name</p>
                )}
              </div>
            </div>
            <div className="text-left  form-q">
              <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">
                Supporting Links
              </label>
              <div>
                <input
                  className="form-input"
                  aria-label="linkedin"
                  type="text"
                  placeholder="Linkedin, personal web, etc..."
                  onChange={handlesupportingLinksChange}
                  value={linkedin}
                />
              </div>
            </div>
          </div>
          {/* Organization type and purchase intent */}
          <div className="flex mb-4 gap-2">
            {!isAcademic && (
              <>
                <div className="text-left  form-q">
                  <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">
                    Organization Type *
                  </label>
                  <div>
                    <select
                      className="form-input"
                      aria-label="profile"
                      onChange={handleProfileChange}
                      value={profile}
                    >
                      <option value="" disabled>
                        Choose one...
                      </option>
                      <option value="NON-PROFIT">University/Institution</option>
                      <option value="FOR-PROFIT">Company</option>
                    </select>
                  </div>
                </div>
                <div className="text-left  form-q">
                  <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">
                    Purchase Intent *
                  </label>
                  <div>
                    <select
                      className="form-input"
                      aria-label="purchaseIntent"
                      onChange={handlePurchaseIntentChange}
                      value={purchaseIntent}
                    >
                      <option value="" disabled>
                        Choose one...
                      </option>
                      <option value="BUDGETED">Budgeted; evaluating options before purchase</option>
                      <option value="NO-BUDGET">No budget; exploring for research purposes only</option>
                      <option value="NO-PLANS">No current purchase plans</option>
                    </select>
                  </div>
                </div>
              </>
            )}
            {isAcademic && (
              <>
                <div className="text-left w-full">
                  <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">
                    Field of Study
                  </label>
                  <div>
                    <input
                      className="form-input"
                      aria-label="fieldOfStudy"
                      type="text"
                      placeholder="Bioinformatics, Genomics..."
                      onChange={handlefieldOfStudyChange}
                      value={fieldOfStudy}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          {/* Password and retype password */}
          <div className="flex mb-4 gap-2">
            <div className="text-left form-q">
              <div className="flex justify-between">
                <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase" htmlFor="password">
                  Password *
                </label>
                <button
                  type="button"
                  className="eye-icon"
                  onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                >
                  {isPasswordVisible ? (
                    <EyeIcon className="w-4 h-4 mb-1" />
                  ) : (
                    <EyeSlashIcon className="w-4 h-4 mb-1" />
                  )}
                </button>
              </div>
              <div data-validate="Password is required">
                <input
                  className="form-input"
                  aria-label="password"
                  id="password"
                  type={isPasswordVisible ? 'text' : 'password'}
                  placeholder=""
                  onChange={handlePasswordChange}
                  onBlur={handlePasswordBlur}
                  value={password}
                />
              </div>
              <div>{!isPasswordValid && <p className="apply-input-error">Please enter a password</p>}</div>
            </div>
            <div className="text-left form-q">
              <div className="flex justify-between">
                <label
                  className="text-xs font-semibold text-gray-600 ml-2 text-uppercase"
                  htmlFor="rpassword"
                >
                  Retype Password *
                </label>
                <button
                  type="button"
                  className="eye-icon"
                  onClick={() => setIsRpasswordVisible(!isRpasswordVisible)}
                >
                  {isRpasswordVisible ? (
                    <EyeIcon className="w-4 h-4 mb-1" />
                  ) : (
                    <EyeSlashIcon className="w-4 h-4 mb-1" />
                  )}
                </button>
              </div>
              <div data-validate="Repeat password">
                <input
                  className="form-input"
                  aria-label="rpassword"
                  id="rpassword"
                  type={isRpasswordVisible ? 'text' : 'password'}
                  placeholder=""
                  onChange={handleRpasswordChange}
                  onBlur={handleRpasswordBlur}
                  value={rpassword}
                />
              </div>
            </div>
          </div>
          {/* Checkboxes */}
          {isAcademic && (
            <div className="form-check text-left">
              <input
                type="checkbox"
                className="form-check-input"
                name="academicLicenseCheckbox"
                checked={academicLicenseAccepted}
                onChange={handleAcademicLicenseCheckboxChange}
              />
              <label className="text-xs">
                I accept the{' '}
                <button
                  type="button"
                  onClick={() => window.open('/academic-license', '_blank', 'noopener,noreferrer')}
                  className="text-sm font-semibold"
                >
                  DISGENET academic License
                </button>
              </label>
            </div>
          )}
          <div className="form-check text-left">
            <input
              className="form-check-input"
              aria-label="privacyPolicyCheckbox"
              type="checkbox"
              name="privacyPolicyCheckbox"
              checked={privacyPolicyAccepted}
              onChange={handlePrivacyPolicyCheckboxChange}
            />
            <label htmlFor="privacy-policy" className="text-xs">
              I have read and accept{' '}
              <button
                type="button"
                onClick={() => window.open('/privacy-policy', '_blank', 'noopener,noreferrer')}
                className="text-sm font-semibold"
              >
                DISGENET&apos;s privacy policy
              </button>
            </label>
          </div>
          <hr></hr>
          <div>{!isRpasswordValid && <p className="apply-input-error">Please re-type your password</p>}</div>
          <div>{!isPasswordMatching && <p className="apply-input-error">Passwords must match</p>}</div>
          <div>{formErrorMessage && <p className="form-error">{formErrorMessage}</p>}</div>
          {showResendForExistingUser && registeredEmail && (
            <ResendConfirmationEmailButton email={registeredEmail} className="mb-4" />
          )}
          {isFormSubmitted && (
            <div className="form-success">
              {isAcademic
                ? 'Check your inbox within 24hrs to complete your application!'
                : 'Please check your inbox now and verify your account'}
            </div>
          )}
          <div className="div-apply-btn flex justify-center">
            <SharedButton
              variant="primary"
              size="md"
              type="button"
              onClick={handleOpenConfirmationPopup}
              disabled={isSubmitting}
            >
              {isAcademic ? academicButton : advancedButton}
            </SharedButton>
          </div>
        </form>
      </div>
      {showConfirmationPopup && (
        <ConfirmationPopup
          onClose={() => setShowConfirmationPopup(false)}
          onConfirm={handleSubmit}
          isAcademic={isAcademic}
        />
      )}
      {showVerifyEmailPopup && registeredEmail && (
        <VerifyEmailPopup email={registeredEmail} onClose={() => setShowVerifyEmailPopup(false)} />
      )}

      {/* LinkedIn Insight Tag noscript fallback */}
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          alt=""
          src="https://px.ads.linkedin.com/collect/?pid=8934697&fmt=gif"
        />
      </noscript>
    </>
  );
}

export default SignUpForm;
